import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getRoomDetail,
  getMessages,
  postMessage,
  deleteMessage,
  getDiceRolls,
  rollDice,
  getSheets,
  createSheet,
  deleteSheet,
  updateRoomMap,
  getTokens,
  createToken,
  updateToken,
  deleteToken,
  uploadAsset,
  createInvite,
  getDrawings,
  createDrawing,
} from "@/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { brandAssets } from "@/constants/brand";
import { useAuth } from "@/context/AuthContext";

const defaultStats = {
  strength: 10,
  dexterity: 10,
  constitution: 10,
  intelligence: 10,
  wisdom: 10,
  charisma: 10,
};

const tokenDefaults = [
  {
    name: "Crimson Warden",
    image_url: brandAssets.portraits[0],
  },
  {
    name: "Violet Seer",
    image_url: brandAssets.portraits[1],
  },
  {
    name: "Ashen Knight",
    image_url:
      "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxmYW50YXN5JTIwYXJtb3IlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzAxMzU4NDJ8MA&ixlib=rb-4.1.0&q=85",
  },
];

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "/vtt-api").replace(/\/+$/, "");


const resolveAssetUrl = (url) => {
  if (!url) {
    return "";
  }
  if (url.startsWith("http")) {
    return url;
  }
  return `${BACKEND_URL}${url}`;
};

export default function RoomPage() {
  const { roomId } = useParams();
  const { user } = useAuth();
  const mapRef = useRef(null);
  const drawingCanvasRef = useRef(null);
  const fogCanvasRef = useRef(null);
  const [room, setRoom] = useState(null);
  const [map, setMap] = useState(null);
  const [messages, setMessages] = useState([]);
  const [diceRolls, setDiceRolls] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [fogDrawings, setFogDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [chatInput, setChatInput] = useState("");
  const [diceExpression, setDiceExpression] = useState("1d20+0");
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [mapUploading, setMapUploading] = useState(false);
  const [drawTool, setDrawTool] = useState("move");
  const [currentStroke, setCurrentStroke] = useState(null);
  const [mapForm, setMapForm] = useState({
    title: "",
    image_url: "",
    grid_size: 50,
    fog_enabled: false,
  });
  const [sheetForm, setSheetForm] = useState({
    name: "",
    class_name: "",
    level: 1,
    notes: "",
    portrait_url: brandAssets.portraits[0],
    ...defaultStats,
  });
  const [tokenForm, setTokenForm] = useState({
    name: "",
    image_url: brandAssets.portraits[0],
    size: 64,
  });
  const [uploading, setUploading] = useState(false);
  const [draggingToken, setDraggingToken] = useState(null);
  const [inviteLink, setInviteLink] = useState("");
  const [inviteStatus, setInviteStatus] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [inviteMeta, setInviteMeta] = useState(null);

  const loadRoom = async () => {
    setLoading(true);
    setStatus("");
    try {
      const [roomRes, messagesRes, diceRes, sheetsRes, tokensRes, drawingsRes] = await Promise.all([
        getRoomDetail(roomId),
        getMessages(roomId),
        getDiceRolls(roomId),
        getSheets(roomId),
        getTokens(roomId),
        getDrawings(roomId),
      ]);
      setRoom(roomRes.data.room);
      setMap(roomRes.data.map);
      setMapForm({
        title: roomRes.data.map.title,
        image_url: roomRes.data.map.image_url,
        grid_size: roomRes.data.map.grid_size,
        fog_enabled: roomRes.data.map.fog_enabled,
      });
      setMessages(messagesRes.data);
      setDiceRolls(diceRes.data);
      setSheets(sheetsRes.data);
      setTokens(tokensRes.data);
      const drawingList = drawingsRes.data || [];
      setDrawings(drawingList.filter((item) => !item.type.startsWith("fog")));
      setFogDrawings(drawingList.filter((item) => item.type.startsWith("fog")));
    } catch (error) {
      setStatus("The room is shrouded in mist.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoom();
  }, [roomId]);

  useEffect(() => {
    const token = localStorage.getItem("dracon_token");
    if (!token) {
      return undefined;
    }
const wsBase = BACKEND_URL.startsWith("http")
  ? BACKEND_URL.replace(/^https/, "wss").replace(/^http/, "ws")
  : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}${BACKEND_URL}`;

const socket = new WebSocket(`${wsBase}/api/ws/${roomId}?token=${token}`);


    socket.onopen = () => setWsConnected(true);
    socket.onclose = () => setWsConnected(false);
    socket.onerror = () => setWsConnected(false);
    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        const { type, payload } = message || {};
        if (!type) {
          return;
        }
        if (type === "chat_message") {
          setMessages((prev) => (prev.some((msg) => msg.id === payload.id) ? prev : [...prev, payload]));
        }
        if (type === "message_delete") {
          setMessages((prev) => prev.filter((msg) => msg.id !== payload.id));
        }
        if (type === "dice_roll") {
          setDiceRolls((prev) => (prev.some((roll) => roll.id === payload.id) ? prev : [...prev, payload]));
        }
        if (type === "map_update") {
          setMap(payload);
          setMapForm({
            title: payload.title,
            image_url: payload.image_url,
            grid_size: payload.grid_size,
            fog_enabled: payload.fog_enabled,
          });
        }
        if (type === "token_create") {
          setTokens((prev) => (prev.some((token) => token.id === payload.id) ? prev : [...prev, payload]));
        }
        if (type === "token_update") {
          setTokens((prev) => prev.map((token) => (token.id === payload.id ? payload : token)));
        }
        if (type === "token_delete") {
          setTokens((prev) => prev.filter((token) => token.id !== payload.id));
        }
        if (type === "sheet_create") {
          setSheets((prev) => (prev.some((sheet) => sheet.id === payload.id) ? prev : [...prev, payload]));
        }
        if (type === "sheet_delete") {
          setSheets((prev) => prev.filter((sheet) => sheet.id !== payload.id));
        }
        if (type === "drawing_create") {
          if (payload.type && payload.type.startsWith("fog")) {
            setFogDrawings((prev) =>
              prev.some((item) => item.id === payload.id) ? prev : [...prev, payload],
            );
          } else {
            setDrawings((prev) =>
              prev.some((item) => item.id === payload.id) ? prev : [...prev, payload],
            );
          }
        }
      } catch (error) {
        // Ignore malformed socket messages
      }
    };

    return () => {
      socket.close();
    };
  }, [roomId]);

  useEffect(() => {
    const container = mapRef.current;
    const drawingCanvas = drawingCanvasRef.current;
    const fogCanvas = fogCanvasRef.current;
    if (!container || !drawingCanvas || !fogCanvas) {
      return undefined;
    }

    const renderStroke = (ctx, stroke, rect, isFogLayer) => {
      if (!stroke.points?.length) {
        return;
      }
      const toPx = (point) => ({
        x: (point.x / 100) * rect.width,
        y: (point.y / 100) * rect.height,
      });
      const points = stroke.points.map(toPx);
      ctx.lineWidth = stroke.stroke_width || 3;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (isFogLayer && stroke.type === "fog_erase") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle =
          stroke.type === "fog" ? "rgba(15,15,16,0.6)" : stroke.color || "#dc2626";
      }
      ctx.beginPath();
      if (stroke.type === "line") {
        ctx.moveTo(points[0].x, points[0].y);
        ctx.lineTo(points[1].x, points[1].y);
      } else if (stroke.type === "rect") {
        const start = points[0];
        const end = points[1];
        ctx.rect(start.x, start.y, end.x - start.x, end.y - start.y);
      } else {
        ctx.moveTo(points[0].x, points[0].y);
        points.slice(1).forEach((point) => ctx.lineTo(point.x, point.y));
      }
      ctx.stroke();
    };

    const render = () => {
      const rect = container.getBoundingClientRect();
      if (!rect.width || !rect.height) {
        return;
      }
      [drawingCanvas, fogCanvas].forEach((canvas) => {
        canvas.width = rect.width;
        canvas.height = rect.height;
      });

      const drawCtx = drawingCanvas.getContext("2d");
      const fogCtx = fogCanvas.getContext("2d");
      if (!drawCtx || !fogCtx) {
        return;
      }
      drawCtx.clearRect(0, 0, rect.width, rect.height);
      fogCtx.clearRect(0, 0, rect.width, rect.height);

      const allDrawings = [...drawings];
      const allFog = [...fogDrawings];
      if (currentStroke) {
        if (currentStroke.type.startsWith("fog")) {
          allFog.push(currentStroke);
        } else {
          allDrawings.push(currentStroke);
        }
      }
      allDrawings.forEach((stroke) => renderStroke(drawCtx, stroke, rect, false));
      allFog.forEach((stroke) => renderStroke(fogCtx, stroke, rect, true));
    };

    render();
    window.addEventListener("resize", render);
    return () => window.removeEventListener("resize", render);
  }, [drawings, fogDrawings, currentStroke, map?.image_url]);

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!chatInput.trim()) {
      return;
    }
    try {
      const { data } = await postMessage(roomId, { content: chatInput });
      setChatInput("");
      setMessages((prev) => (prev.some((msg) => msg.id === data.id) ? prev : [...prev, data]));
    } catch (error) {
      setStatus("Message lost in the void.");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteMessage(messageId);
      setMessages((prev) => prev.filter((message) => message.id !== messageId));
    } catch (error) {
      setStatus("Unable to delete that message.");
    }
  };

  const handleRollDice = async (event) => {
    event.preventDefault();
    if (!diceExpression.trim()) {
      return;
    }
    try {
      const { data } = await rollDice(roomId, { expression: diceExpression });
      setDiceRolls((prev) => (prev.some((roll) => roll.id === data.id) ? prev : [...prev, data]));
    } catch (error) {
      setStatus("The dice refused to fall.");
    }
  };

  const handleMapUpdate = async (event) => {
    event.preventDefault();
    try {
      const { data } = await updateRoomMap(roomId, {
        title: mapForm.title,
        image_url: mapForm.image_url,
        grid_size: Number(mapForm.grid_size),
        fog_enabled: mapForm.fog_enabled,
      });
      setMap(data);
    } catch (error) {
      setStatus("Map wards failed to update.");
    }
  };

  const handleMapUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setMapUploading(true);
    try {
      const { data } = await uploadAsset(file);
      setMapForm((prev) => ({ ...prev, image_url: data.url }));
    } catch (error) {
      setStatus("Map upload failed.");
    } finally {
      setMapUploading(false);
    }
  };

  const handleTokenUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const { data } = await uploadAsset(file);
      setTokenForm((prev) => ({ ...prev, image_url: data.url }));
    } catch (error) {
      setStatus("Token upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateToken = async (event) => {
    event.preventDefault();
    try {
      const { data } = await createToken(roomId, {
        name: tokenForm.name || "Token",
        image_url: tokenForm.image_url,
        size: Number(tokenForm.size),
      });
      setTokenForm({ name: "", image_url: brandAssets.portraits[0], size: 64 });
      setTokens((prev) => (prev.some((token) => token.id === data.id) ? prev : [...prev, data]));
    } catch (error) {
      setStatus("Unable to forge that token.");
    }
  };

  const handleDeleteToken = async (tokenId) => {
    try {
      await deleteToken(tokenId);
      setTokens((prev) => prev.filter((token) => token.id !== tokenId));
    } catch (error) {
      setStatus("Unable to banish that token.");
    }
  };

  const handleTokenMouseDown = (event, token) => {
    const canEditToken = isGM || token.owner_id === user?.id;
    if (!canEditToken || !mapRef.current || drawTool !== "move") {
      return;
    }
    const rect = mapRef.current.getBoundingClientRect();
    const tokenX = (token.x / 100) * rect.width;
    const tokenY = (token.y / 100) * rect.height;
    setDraggingToken({
      id: token.id,
      offsetX: event.clientX - rect.left - tokenX,
      offsetY: event.clientY - rect.top - tokenY,
    });
  };

  const handleMapMouseMove = (event) => {
    if (drawTool !== "move") {
      handleDrawMove(event);
      return;
    }
    if (!draggingToken || !mapRef.current) {
      return;
    }
    const rect = mapRef.current.getBoundingClientRect();
    const xPx = event.clientX - rect.left - draggingToken.offsetX;
    const yPx = event.clientY - rect.top - draggingToken.offsetY;
    const xPct = Math.min(100, Math.max(0, (xPx / rect.width) * 100));
    const yPct = Math.min(100, Math.max(0, (yPx / rect.height) * 100));
    let snappedX = xPct;
    let snappedY = yPct;
    if (snapEnabled && map?.grid_size) {
      const stepX = (map.grid_size / rect.width) * 100;
      const stepY = (map.grid_size / rect.height) * 100;
      if (stepX > 0 && stepY > 0) {
        snappedX = Math.round(xPct / stepX) * stepX;
        snappedY = Math.round(yPct / stepY) * stepY;
      }
    }
    setTokens((prev) =>
      prev.map((token) =>
        token.id === draggingToken.id ? { ...token, x: snappedX, y: snappedY } : token,
      ),
    );
  };

  const commitTokenPosition = async () => {
    if (drawTool !== "move") {
      handleDrawEnd();
      return;
    }
    if (!draggingToken) {
      return;
    }
    const movedToken = tokens.find((token) => token.id === draggingToken.id);
    setDraggingToken(null);
    if (!movedToken) {
      return;
    }
    try {
      await updateToken(movedToken.id, { x: movedToken.x, y: movedToken.y });
    } catch (error) {
      setStatus("Token movement failed.");
    }
  };

  const getPointFromEvent = (event) => {
    if (!mapRef.current) {
      return null;
    }
    const rect = mapRef.current.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleDrawStart = (event) => {
    if (drawTool === "move") {
      return;
    }
    const point = getPointFromEvent(event);
    if (!point) {
      return;
    }
    const type = drawTool === "erase" ? "fog_erase" : drawTool;
    const baseStroke = {
      type,
      points: drawTool === "line" || drawTool === "rect" ? [point, point] : [point],
      color: drawTool === "fog" || drawTool === "erase" ? "#0f0f10" : "#dc2626",
      stroke_width: drawTool === "fog" || drawTool === "erase" ? 40 : 3,
    };
    setCurrentStroke(baseStroke);
  };

  const handleDrawMove = (event) => {
    if (!currentStroke || drawTool === "move") {
      return;
    }
    const point = getPointFromEvent(event);
    if (!point) {
      return;
    }
    if (currentStroke.type === "line" || currentStroke.type === "rect") {
      setCurrentStroke({ ...currentStroke, points: [currentStroke.points[0], point] });
      return;
    }
    setCurrentStroke({ ...currentStroke, points: [...currentStroke.points, point] });
  };

  const handleDrawEnd = async () => {
    if (!currentStroke || drawTool === "move") {
      setCurrentStroke(null);
      return;
    }
    try {
      const { data } = await createDrawing(roomId, {
        type: currentStroke.type,
        points: currentStroke.points,
        color: currentStroke.color,
        stroke_width: currentStroke.stroke_width,
      });
      if (data.type.startsWith("fog")) {
        setFogDrawings((prev) => (prev.some((item) => item.id === data.id) ? prev : [...prev, data]));
      } else {
        setDrawings((prev) => (prev.some((item) => item.id === data.id) ? prev : [...prev, data]));
      }
    } catch (error) {
      setStatus("Drawing failed to bind.");
    } finally {
      setCurrentStroke(null);
    }
  };

  const handleCreateInvite = async () => {
    setInviteStatus("");
    try {
      const { data } = await createInvite(roomId);
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      setInviteLink(`${origin}/invite/${data.code}`);
      setInviteMeta({
        expires_at: data.expires_at,
        remaining_uses: data.remaining_uses,
      });
    } catch (error) {
      setInviteStatus("Unable to create invite link.");
    }
  };

  const handleCopyInvite = async () => {
    if (!inviteLink || typeof navigator === "undefined") {
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteStatus("Invite link copied.");
    } catch (error) {
      setInviteStatus("Copy failed. Please copy manually.");
    }
  };

  const handleCreateSheet = async (event) => {
    event.preventDefault();
    try {
      const { data } = await createSheet(roomId, {
        name: sheetForm.name,
        class_name: sheetForm.class_name,
        level: Number(sheetForm.level),
        stats: {
          strength: Number(sheetForm.strength),
          dexterity: Number(sheetForm.dexterity),
          constitution: Number(sheetForm.constitution),
          intelligence: Number(sheetForm.intelligence),
          wisdom: Number(sheetForm.wisdom),
          charisma: Number(sheetForm.charisma),
        },
        notes: sheetForm.notes,
        portrait_url: sheetForm.portrait_url,
      });
      setSheetForm({
        name: "",
        class_name: "",
        level: 1,
        notes: "",
        portrait_url: brandAssets.portraits[0],
        ...defaultStats,
      });
      setSheets((prev) => (prev.some((sheet) => sheet.id === data.id) ? prev : [...prev, data]));
    } catch (error) {
      setStatus("The sheet resisted binding.");
    }
  };

  const handleDeleteSheet = async (sheetId) => {
    try {
      await deleteSheet(sheetId);
      setSheets((prev) => prev.filter((sheet) => sheet.id !== sheetId));
    } catch (error) {
      setStatus("Unable to banish that sheet.");
    }
  };

  const isGM = room?.gm_ids?.includes(user?.id) || room?.owner_id === user?.id;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-stone-300 font-cinzel"
        data-testid="room-loading-state"
      >
        Calibrating the tabletop...
      </div>
    );
  }

  return (
    <AppShell variant="room">
      <div className="relative min-h-screen" data-testid="room-page">
        <div className="max-w-6xl mx-auto px-6 py-8" data-testid="room-header">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-stone-500" data-testid="room-eyebrow">
                War Room
              </p>
              <h1 className="font-cinzel text-3xl" data-testid="room-title">
                {room?.name}
              </h1>
              <p className="text-stone-400 mt-2" data-testid="room-description">
                {room?.description || "The realm has not yet been chronicled."}
              </p>
              <p className="text-xs text-stone-500 mt-2" data-testid="room-live-status">
                Live sync: {wsConnected ? "Connected" : "Offline"}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={loadRoom}
              className="border border-stone-700 text-stone-200 hover:text-stone-100"
              data-testid="room-refresh-button"
            >
              Refresh Room
            </Button>
          </div>
          {isGM && (
            <div className="panel-glass rounded-xl p-4 mt-6" data-testid="invite-panel">
              <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="invite-label">
                Invite Link
              </p>
              <div className="flex flex-col md:flex-row gap-3 mt-3">
                <Input
                  value={inviteLink}
                  onChange={(event) => setInviteLink(event.target.value)}
                  placeholder="Generate a link for your party"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="invite-link-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="border border-stone-700 text-stone-200 hover:text-stone-100"
                  onClick={handleCreateInvite}
                  data-testid="invite-generate-button"
                >
                  Generate
                </Button>
                <Button
                  type="button"
                  className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                  onClick={handleCopyInvite}
                  data-testid="invite-copy-button"
                >
                  Copy Link
                </Button>
              </div>
              {inviteMeta && (
                <p className="text-xs text-stone-500 mt-2" data-testid="invite-meta">
                  Expires {new Date(inviteMeta.expires_at).toLocaleString()} · {inviteMeta.remaining_uses} use(s)
                </p>
              )}
              {inviteStatus && (
                <p className="text-xs text-stone-400 mt-2" data-testid="invite-status">
                  {inviteStatus}
                </p>
              )}
            </div>
          )}
          {status && (
            <div
              className="bg-red-900/30 border border-red-900/50 text-red-200 px-4 py-3 rounded-lg mt-6"
              data-testid="room-status-message"
            >
              {status}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[2.2fr_1fr] gap-8 max-w-6xl mx-auto px-6 pb-16" data-testid="room-layout">
          <div className="space-y-8" data-testid="room-main-column">
            <div className="panel-iron rounded-2xl overflow-hidden" data-testid="map-panel">
              <div
                ref={mapRef}
                className="h-[420px] w-full map-frame relative overflow-hidden"
                style={{
                  backgroundImage: `url(${resolveAssetUrl(map?.image_url || brandAssets.mapPlaceholder)})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
                onMouseMove={handleMapMouseMove}
                onMouseUp={commitTokenPosition}
                onMouseLeave={commitTokenPosition}
                onMouseDown={handleDrawStart}
                data-testid="room-map-display"
              >
                {tokens.map((token) => (
                  <img
                    key={token.id}
                    src={resolveAssetUrl(token.image_url)}
                    alt={token.name}
                    className="absolute rounded-full border border-stone-200/40 shadow-lg cursor-grab"
                    style={{
                      left: `${token.x}%`,
                      top: `${token.y}%`,
                      width: `${token.size}px`,
                      height: `${token.size}px`,
                      transform: "translate(-50%, -50%)",
                      pointerEvents: drawTool === "move" ? "auto" : "none",
                    }}
                    onMouseDown={(event) => handleTokenMouseDown(event, token)}
                    draggable={false}
                    data-testid={`map-token-${token.id}`}
                  />
                ))}
                <canvas
                  ref={drawingCanvasRef}
                  className="absolute inset-0 pointer-events-none"
                  data-testid="map-drawing-canvas"
                />
                <canvas
                  ref={fogCanvasRef}
                  className="absolute inset-0 pointer-events-none"
                  data-testid="map-fog-canvas"
                />
              </div>
              <div className="p-6 space-y-4" data-testid="map-details">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="map-label">
                    Shared Map
                  </p>
                  <h2 className="font-cinzel text-2xl" data-testid="map-title">
                    {map?.title}
                  </h2>
                </div>
                <p className="text-stone-400 text-sm" data-testid="map-grid-info">
                  Grid size: {map?.grid_size}px · Fog: {map?.fog_enabled ? "Active" : "Dormant"}
                </p>
                <div className="flex items-center gap-3" data-testid="snap-toggle">
                  <Switch
                    checked={snapEnabled}
                    onCheckedChange={(value) => setSnapEnabled(value)}
                    data-testid="snap-toggle-switch"
                  />
                  <span className="text-stone-300 text-sm" data-testid="snap-toggle-label">
                    Token snap to grid
                  </span>
                </div>
                {isGM ? (
                  <form
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    onSubmit={handleMapUpdate}
                    data-testid="map-form"
                  >
                    <Input
                      value={mapForm.title}
                      onChange={(event) => setMapForm((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder="Map title"
                      className="bg-stone-950 border-stone-800 text-stone-200"
                      data-testid="map-title-input"
                    />
                    <Input
                      value={mapForm.image_url}
                      onChange={(event) => setMapForm((prev) => ({ ...prev, image_url: event.target.value }))}
                      placeholder="Map image URL"
                      className="bg-stone-950 border-stone-800 text-stone-200"
                      data-testid="map-image-input"
                    />
                    <div className="md:col-span-2" data-testid="map-upload-field">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleMapUpload}
                        className="bg-stone-950 border-stone-800 text-stone-200"
                        data-testid="map-upload-input"
                      />
                      <p className="text-xs text-stone-500 mt-2" data-testid="map-upload-note">
                        {mapUploading ? "Uploading map..." : "Upload a new map image (local storage)."}
                      </p>
                    </div>
                    <Input
                      type="number"
                      value={mapForm.grid_size}
                      onChange={(event) => setMapForm((prev) => ({ ...prev, grid_size: event.target.value }))}
                      placeholder="Grid size"
                      className="bg-stone-950 border-stone-800 text-stone-200"
                      data-testid="map-grid-input"
                    />
                    <div className="flex items-center gap-3" data-testid="map-fog-toggle">
                      <Switch
                        checked={mapForm.fog_enabled}
                        onCheckedChange={(value) => setMapForm((prev) => ({ ...prev, fog_enabled: value }))}
                        data-testid="map-fog-switch"
                      />
                      <span className="text-stone-300 text-sm" data-testid="map-fog-label">
                        Fog enabled
                      </span>
                    </div>
                    <Button
                      type="submit"
                      className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                      data-testid="map-save-button"
                    >
                      Update Map
                    </Button>
                  </form>
                ) : (
                  <p className="text-stone-500 text-sm" data-testid="map-permission-note">
                    Only the GM can edit the map and fog settings.
                  </p>
                )}
                <div className="mt-4" data-testid="drawing-toolbar">
                  <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="drawing-toolbar-label">
                    Drawing Tools
                  </p>
                  <div className="flex flex-wrap gap-3 mt-3" data-testid="drawing-toolbar-buttons">
                    {[
                      { id: "move", label: "Move Tokens" },
                      { id: "pen", label: "Freehand" },
                      { id: "line", label: "Line" },
                      { id: "rect", label: "Rectangle" },
                      { id: "fog", label: "Fog Brush" },
                      { id: "erase", label: "Fog Erase" },
                    ].map((tool) => (
                      <Button
                        key={tool.id}
                        type="button"
                        variant="secondary"
                        className={
                          drawTool === tool.id
                            ? "border border-red-700 text-stone-100"
                            : "border border-stone-700 text-stone-300"
                        }
                        onClick={() => setDrawTool(tool.id)}
                        data-testid={`drawing-tool-${tool.id}`}
                      >
                        {tool.label}
                      </Button>
                    ))}
                  </div>
                  <p className="text-xs text-stone-500 mt-3" data-testid="drawing-toolbar-note">
                    Active tool: {drawTool === "move" ? "Token Move" : drawTool}
                  </p>
                </div>
              </div>
            </div>

            <div className="panel-iron rounded-2xl p-6" data-testid="tokens-panel">
              <div className="flex items-center justify-between" data-testid="tokens-header">
                <h2 className="font-cinzel text-2xl" data-testid="tokens-title">
                  Map Tokens
                </h2>
                <p className="text-stone-500 text-sm" data-testid="tokens-count">
                  {tokens.length} sigils
                </p>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" onSubmit={handleCreateToken} data-testid="token-form">
                <Input
                  value={tokenForm.name}
                  onChange={(event) => setTokenForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Token name"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="token-name-input"
                />
                <Input
                  value={tokenForm.image_url}
                  onChange={(event) => setTokenForm((prev) => ({ ...prev, image_url: event.target.value }))}
                  placeholder="Token image URL"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="token-image-input"
                />
                <Input
                  type="number"
                  value={tokenForm.size}
                  onChange={(event) => setTokenForm((prev) => ({ ...prev, size: event.target.value }))}
                  placeholder="Token size"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="token-size-input"
                />
                <div className="md:col-span-2" data-testid="token-upload-field">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleTokenUpload}
                    className="bg-stone-950 border-stone-800 text-stone-200"
                    data-testid="token-upload-input"
                  />
                  <p className="text-xs text-stone-500 mt-2" data-testid="token-upload-note">
                    {uploading ? "Uploading..." : "Upload a token image to use local storage."}
                  </p>
                </div>
                <Button
                  type="submit"
                  className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                  data-testid="token-submit-button"
                >
                  Forge Token
                </Button>
              </form>
              <div className="mt-6" data-testid="token-defaults">
                <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="token-defaults-label">
                  Default Sigils
                </p>
                <div className="flex flex-wrap gap-4 mt-3" data-testid="token-defaults-grid">
                  {tokenDefaults.map((token, index) => (
                    <button
                      key={token.name}
                      type="button"
                      className="flex items-center gap-3 border border-stone-700 rounded-full pr-4 bg-stone-950/70 hover:border-red-900/60 transition-colors duration-300"
                      onClick={() => setTokenForm((prev) => ({ ...prev, image_url: token.image_url, name: token.name }))}
                      data-testid={`token-default-${index}`}
                    >
                      <img
                        src={resolveAssetUrl(token.image_url)}
                        alt={token.name}
                        className="h-12 w-12 rounded-full border border-stone-700 object-cover"
                        data-testid={`token-default-image-${index}`}
                      />
                      <span className="text-sm text-stone-300" data-testid={`token-default-name-${index}`}>
                        {token.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="token-list">
                {tokens.map((token) => {
                  const canEditToken = isGM || token.owner_id === user?.id;
                  return (
                    <div key={token.id} className="flex items-center justify-between border border-stone-800 rounded-lg p-3" data-testid={`token-card-${token.id}`}>
                      <div className="flex items-center gap-3">
                        <img
                          src={resolveAssetUrl(token.image_url)}
                          alt={token.name}
                          className="h-10 w-10 rounded-full border border-stone-700 object-cover"
                          data-testid={`token-card-image-${token.id}`}
                        />
                        <div>
                          <p className="text-sm text-stone-200" data-testid={`token-card-name-${token.id}`}>
                            {token.name}
                          </p>
                          <p className="text-xs text-stone-500" data-testid={`token-card-size-${token.id}`}>
                            Size {token.size}px
                          </p>
                        </div>
                      </div>
                      {canEditToken && (
                        <Button
                          type="button"
                          variant="secondary"
                          className="border border-stone-700 text-stone-200 hover:text-stone-100"
                          onClick={() => handleDeleteToken(token.id)}
                          data-testid={`token-delete-button-${token.id}`}
                        >
                          Banish
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="panel-iron rounded-2xl p-6" data-testid="character-sheets-panel">
              <div className="flex items-center justify-between" data-testid="character-sheets-header">
                <h2 className="font-cinzel text-2xl" data-testid="character-sheets-title">
                  Character Sheets
                </h2>
                <p className="text-stone-500 text-sm" data-testid="character-sheets-count">
                  {sheets.length} bound spirits
                </p>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" onSubmit={handleCreateSheet} data-testid="character-sheet-form">
                <Input
                  value={sheetForm.name}
                  onChange={(event) => setSheetForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Character name"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="sheet-name-input"
                  required
                />
                <Input
                  value={sheetForm.class_name}
                  onChange={(event) => setSheetForm((prev) => ({ ...prev, class_name: event.target.value }))}
                  placeholder="Class"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="sheet-class-input"
                  required
                />
                <Input
                  type="number"
                  value={sheetForm.level}
                  onChange={(event) => setSheetForm((prev) => ({ ...prev, level: event.target.value }))}
                  placeholder="Level"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="sheet-level-input"
                  required
                />
                <Input
                  value={sheetForm.portrait_url}
                  onChange={(event) => setSheetForm((prev) => ({ ...prev, portrait_url: event.target.value }))}
                  placeholder="Portrait URL"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="sheet-portrait-input"
                />
                {Object.keys(defaultStats).map((statKey) => (
                  <Input
                    key={statKey}
                    type="number"
                    value={sheetForm[statKey]}
                    onChange={(event) =>
                      setSheetForm((prev) => ({ ...prev, [statKey]: event.target.value }))
                    }
                    placeholder={statKey}
                    className="bg-stone-950 border-stone-800 text-stone-200"
                    data-testid={`sheet-${statKey}-input`}
                  />
                ))}
                <Textarea
                  value={sheetForm.notes}
                  onChange={(event) => setSheetForm((prev) => ({ ...prev, notes: event.target.value }))}
                  placeholder="Notes"
                  className="bg-stone-950 border-stone-800 text-stone-200 md:col-span-2"
                  data-testid="sheet-notes-input"
                />
                <Button
                  type="submit"
                  className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em] md:col-span-2"
                  data-testid="sheet-submit-button"
                >
                  Bind Character
                </Button>
              </form>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-testid="character-sheets-grid">
                {sheets.map((sheet) => (
                  <div key={sheet.id} className="parchment-card rounded-xl p-6" data-testid={`sheet-card-${sheet.id}`}>
                    <div className="flex items-center gap-4">
                      <img
                        src={resolveAssetUrl(sheet.portrait_url || brandAssets.portraits[0])}
                        alt={sheet.name}
                        className="h-16 w-16 rounded-full border border-stone-800 object-cover"
                        data-testid={`sheet-portrait-${sheet.id}`}
                      />
                      <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-stone-600" data-testid={`sheet-label-${sheet.id}`}>
                          {sheet.class_name}
                        </p>
                        <h3 className="font-cinzel text-xl text-stone-900" data-testid={`sheet-name-${sheet.id}`}>
                          {sheet.name}
                        </h3>
                        <p className="text-stone-700" data-testid={`sheet-level-${sheet.id}`}>
                          Level {sheet.level}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-stone-700 mt-4" data-testid={`sheet-stats-${sheet.id}`}>
                      {Object.entries(sheet.stats || {}).map(([key, value]) => (
                        <div key={key} data-testid={`sheet-stat-${sheet.id}-${key}`}>
                          <span className="uppercase tracking-[0.2em]">{key.slice(0, 3)}</span>: {value}
                        </div>
                      ))}
                    </div>
                    <p className="text-stone-700 mt-3" data-testid={`sheet-notes-${sheet.id}`}>
                      {sheet.notes || "No notes recorded."}
                    </p>
                    {(isGM || sheet.owner_id === user?.id) && (
                      <Button
                        type="button"
                        variant="secondary"
                        className="mt-4 border border-stone-700 text-stone-800 hover:text-stone-900"
                        onClick={() => handleDeleteSheet(sheet.id)}
                        data-testid={`sheet-delete-button-${sheet.id}`}
                      >
                        Banish
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8" data-testid="room-side-column">
            <div className="panel-glass rounded-2xl p-6" data-testid="chat-panel">
              <div className="flex items-center justify-between" data-testid="chat-header">
                <h2 className="font-cinzel text-xl" data-testid="chat-title">
                  Covenant Chat
                </h2>
                <p className="text-stone-500 text-xs" data-testid="chat-count">
                  {messages.length} whispers
                </p>
              </div>
              <div className="mt-4 space-y-3 max-h-72 overflow-y-auto" data-testid="chat-messages">
                {messages.map((message) => {
                  const canDelete = isGM || message.user_id === user?.id;
                  return (
                    <div key={message.id} className="border-b border-stone-800 pb-3" data-testid={`chat-message-${message.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid={`chat-message-user-${message.id}`}>
                          {message.username}
                        </p>
                        {canDelete && (
                          <button
                            type="button"
                            className="text-xs text-red-300 hover:text-red-200 transition-colors duration-300"
                            onClick={() => handleDeleteMessage(message.id)}
                            data-testid={`chat-message-delete-${message.id}`}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                      <p className="text-stone-200" data-testid={`chat-message-content-${message.id}`}>
                        {message.content}
                      </p>
                    </div>
                  );
                })}
              </div>
              <form className="mt-4 flex flex-col gap-3" onSubmit={handleSendMessage} data-testid="chat-form">
                <Textarea
                  value={chatInput}
                  onChange={(event) => setChatInput(event.target.value)}
                  placeholder="Speak to the table"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="chat-input"
                />
                <Button
                  type="submit"
                  className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                  data-testid="chat-submit-button"
                >
                  Send Whisper
                </Button>
              </form>
            </div>

            <div className="panel-glass rounded-2xl p-6" data-testid="dice-panel">
              <div className="flex items-center justify-between" data-testid="dice-header">
                <h2 className="font-cinzel text-xl" data-testid="dice-title">
                  Crimson Dice
                </h2>
                <p className="text-stone-500 text-xs" data-testid="dice-count">
                  {diceRolls.length} rolls
                </p>
              </div>
              <form className="mt-4 flex flex-col gap-3" onSubmit={handleRollDice} data-testid="dice-form">
                <Input
                  value={diceExpression}
                  onChange={(event) => setDiceExpression(event.target.value)}
                  placeholder="2d6+1"
                  className="bg-stone-950 border-stone-800 text-stone-200"
                  data-testid="dice-expression-input"
                />
                <Button
                  type="submit"
                  className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
                  data-testid="dice-roll-button"
                >
                  Roll Dice
                </Button>
              </form>
              <div className="mt-4 space-y-3 max-h-56 overflow-y-auto" data-testid="dice-rolls">
                {diceRolls.map((roll) => (
                  <div key={roll.id} className="border-b border-stone-800 pb-3" data-testid={`dice-roll-${roll.id}`}>
                    <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid={`dice-roll-user-${roll.id}`}>
                      {roll.username}
                    </p>
                    <p className="text-stone-200" data-testid={`dice-roll-expression-${roll.id}`}>
                      {roll.expression} → {roll.total}
                    </p>
                    <p className="text-stone-400 text-xs" data-testid={`dice-roll-results-${roll.id}`}>
                      Rolls: {roll.rolls?.join(", ")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
